import { Center } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { createElement, useMemo, useRef, useState } from 'react'
import type { Group } from 'three'

import { SphereHitbox } from '@components/helpers'
import { WagonAModel, WagonBModel } from '@components/models'
import { UncoupleIndicator } from '@components/ui'
import { findConnectionChain, isLocomotiveMoving } from '@engine'
import { useIsTouch, useVehicleKinematics } from '@hooks'
import { useGameStore, useSoundBoard } from '@stores'
import { RAIL_HEIGHT, randomOneOf } from '@utils'

const DEFAULT_INDICATOR_Z = 0.45

interface WagonProps {
  id: string
  color?: string
}

export function Wagon({ id, color }: WagonProps) {
  const isTouch = useIsTouch()

  const ref = useRef<Group>(null)
  useVehicleKinematics(ref, id)

  const transitioning = useGameStore(state => state.forceTransition)
  const levelComplete = useGameStore(state => state.levelComplete)
  const handleUncoupleWagon = useGameStore(state => state.handleUncoupleWagon)

  const prevCanUncouple = useRef<boolean | undefined>(undefined)
  const [canUncouple, setCanUncouple] = useState<boolean | undefined>(undefined)
  const prevIsCoupled = useRef(false)
  const [isCoupled, setIsCoupled] = useState(false)
  const prevIsMoving = useRef(false)
  const [isMoving, setIsMoving] = useState(false)

  const [hoverHighlight, setHoverHighlight] = useState(false)
  const canInteract = !levelComplete && !isMoving && !!canUncouple
  const highlight = isTouch ? canInteract : hoverHighlight

  const prevIndicatorOffsetZ = useRef(DEFAULT_INDICATOR_Z)
  const [indicatorOffsetZ, setIndicatorOffsetZ] = useState(DEFAULT_INDICATOR_Z)

  const sounds = useSoundBoard(state => state.sounds)

  useFrame(() => {
    const state = useGameStore.getState()
    if (!state.network) return

    const wagon = state.network.wagons.get(id)
    if (!wagon) return

    const parentId = state.network.locomotive
      ? findConnectionChain(state.network, state.network.locomotive.id).get(id)
      : undefined

    const nextIsCoupled = !!parentId
    const nextIsMoving = isLocomotiveMoving(state.network.locomotive)

    if (nextIsCoupled !== prevIsCoupled.current) {
      prevIsCoupled.current = nextIsCoupled
      setIsCoupled(nextIsCoupled)
    }

    if (nextIsMoving !== prevIsMoving.current) {
      prevIsMoving.current = nextIsMoving
      setIsMoving(nextIsMoving)
    }

    const nextIndicatorOffsetZ = parentId
      ? wagon.linkedFront === parentId
        ? DEFAULT_INDICATOR_Z
        : -DEFAULT_INDICATOR_Z
      : prevIndicatorOffsetZ.current

    if (nextIndicatorOffsetZ !== prevIndicatorOffsetZ.current) {
      prevIndicatorOffsetZ.current = nextIndicatorOffsetZ
      setIndicatorOffsetZ(nextIndicatorOffsetZ)
    }

    const nextCanUncouple = wagon.canUncouple
    if (nextCanUncouple !== prevCanUncouple.current) {
      prevCanUncouple.current = nextCanUncouple

      setCanUncouple(nextCanUncouple)
      if (nextCanUncouple === undefined) {
        setHoverHighlight(false)
      }
    }
  })

  const Model = useMemo(() => randomOneOf([WagonAModel, WagonBModel]), [])

  return (
    <group ref={ref}>
      {canInteract && (
        <SphereHitbox
          position-y={RAIL_HEIGHT + 0.31}
          radius={0.5}
          onPointerOver={() => setHoverHighlight(true)}
          onPointerOut={() => setHoverHighlight(false)}
          onClick={event => {
            event.stopPropagation()
            handleUncoupleWagon(id)
            setHoverHighlight(false)
            sounds?.uncouple.play()
          }}
        />
      )}

      <Center position-y={RAIL_HEIGHT + 0.31}>
        {createElement(Model, { scale: [0.4, 0.35, 0.3], color, highlight })}
      </Center>

      <UncoupleIndicator
        position={[0, 0.8, indicatorOffsetZ]}
        scale={isMoving ? 0.7 : 1}
        type={
          !isCoupled || transitioning || levelComplete
            ? 'none'
            : canUncouple
              ? highlight
                ? 'uncoupled'
                : 'allow'
              : 'deny'
        }
        onPointerOver={canInteract ? () => setHoverHighlight(true) : undefined}
        onPointerOut={canInteract ? () => setHoverHighlight(false) : undefined}
        onClick={
          canInteract
            ? () => {
                handleUncoupleWagon(id)
                setHoverHighlight(false)
                sounds?.uncouple.play()
              }
            : undefined
        }
      />
    </group>
  )
}
